import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  Button,
  Descriptions,
  Avatar,
  Upload,
  message,
  Form,
  Input,
  Modal,
  Divider,
  Statistic,
  Badge,
  Space,
  Typography
} from 'antd';
import {
  UserOutlined,
  EditOutlined,
  PlusOutlined,
  UploadOutlined,
  DollarOutlined,
  FileTextOutlined,
  EyeOutlined,
  LockOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { agencyService } from '../services/agencyService';
import { useSimpleSearch } from '../hooks/usePageSearch';
import { useTranslation } from 'react-i18next';
import '../assets/styles/Profile.css';

const { Title, Text } = Typography;

interface UserProfile {
  id: number;
  login: string;
  firstName?: string;
  lastName?: string;
  email: string;
  imageUrl?: string;
  authorities: string[];
}

interface AgencyData {
  id: number;
  agencyName: string;
  licenceNumber: string;
  address: string;
  phoneNum: string;
  city: string;
  agencyEmail: string;
  managerFirstName: string;
  managerLastName: string;
  zipCode: string;
  status: string;
  balance: number;
  validationDateTime?: string;
  currency?: {
    code: string;
    symbol: string;
  };
  country?: {
    country: string;
    code: string;
  };
}
const Profile: React.FC = () => {
  const { hasRole } = useAuth();
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [agencyData, setAgencyData] = useState<AgencyData | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [licenceModalVisible, setLicenceModalVisible] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState<string>('');
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [fileList, setFileList] = useState<any[]>([]);
  const [logoUrl, setLogoUrl] = useState<string>('');

  // Hook de recherche global
  useSimpleSearch((term: string) => {
    setLocalSearchTerm(term);
  });

  const isAdmin = hasRole('ROLE_ADMIN');
  const isAgency = hasRole('ROLE_AGENCY');

  useEffect(() => {
    fetchUserProfile();
    if (isAgency) {
      fetchAgencyData();
    }
  }, [isAgency]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const response = await authService.getCurrentUser();
      setUserProfile(response.data);
    } catch (error) {
      message.error(t('profile.profileLoadError'));
      console.error('Error fetching user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgencyData = async () => {
    try {
      const response = await agencyService.getCurrentAgency();
      setAgencyData(response.data);
      setLogoUrl(response.data.imageUrl || '');
    } catch (error) {
      message.error(t('profile.agencyLoadError'));
      console.error('Error fetching agency data:', error);
    }
  };

  const mapFormToAgencyPayload = (values) => {
    return {
      agencyName: values.agencyName,
      licenceNumber: values.licenceNumber,
      address: values.address,
      zipCode: values.postalCode,          
      phoneNum: values.phone,              
      city: values.city,
      agencyEmail: values.agencyEmail,
      title: 'Mr',                         
      managerFirstName: values.managerFirstName,
      managerLastName: values.managerLastName,
      language: 'fr',
      countryId: 1001, //pour tester apres il faut voir
      currencyId: 1,
      password: values.password
    };
  };

  const handleUpdateProfile = async (values: any) => {
    try {
      setLoading(true);
      if (isAgency && agencyData) {
        // Utiliser l'endpoint de mise à jour d'agence
        const payload = mapFormToAgencyPayload(values);
        await agencyService.update(agencyData.id, payload);
        message.success(t('profile.profileUpdateSuccess'));
        setEditModalVisible(false);
        fetchAgencyData();
      }
    } catch (error) {
      message.error(t('profile.profileUpdateError'));
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (values: any) => {
    try {
      setLoading(true);
      // Pour l'instant, on simule l'appel API du changement de mot de passe
       await authService.changePassword({
         currentPassword: values.currentPassword,
         newPassword: values.newPassword
       });
      console.log('Change password:', values);
      message.success(t('profile.passwordChangeSuccess'));
      setPasswordModalVisible(false);
      passwordForm.resetFields();
    } catch (error) {
      message.error(t('profile.passwordChangeError'));
      console.error('Error changing password:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadChange = (info: any) => {
    setFileList(info.fileList);
    if (info.file.status === 'uploading') {
      setLoading(true);
      return;
    }
    if (info.file.status === 'done') {
      message.success(t('profile.licenceUploadSuccess'));
      setLoading(false);
    }
  };

  const beforeUpload = (file: any) => {
    const isPdf = file.type === 'application/pdf';
    const isImage = file.type.startsWith('image/');
    if (!isPdf && !isImage) {
      message.error(t('profile.licenceFileTypeError'));
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error(t('profile.licenceFileSizeError'));
      return false;
    }
    return true;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'ACTIVE': { color: 'green', text: t('profile.statusActive') },
      'WAITING_ACTIVATION': { color: 'orange', text: t('profile.statusWaitingActivation') },
      'SUSPENDED': { color: 'red', text: t('profile.statusSuspended') },
      'TERMINATED': { color: 'red', text: t('profile.statusTerminated') }
    };
    const config = statusConfig[status as keyof typeof statusConfig] || { color: 'gray', text: status };
    return <Badge color={config.color} text={config.text} />;
  };

  // Fonction de filtrage pour la recherche
  const isFieldVisible = (fieldValue: string | undefined, label: string) => {
    if (!localSearchTerm) return true;
    
    const searchLower = localSearchTerm.toLowerCase();
    const fieldValueLower = (fieldValue || '').toLowerCase();
    const labelLower = label.toLowerCase();
    
    return fieldValueLower.includes(searchLower) || labelLower.includes(searchLower);
  };

  const renderFilteredDescriptions = (items: Array<{label: string, value: string | undefined}>) => {
    const filteredItems = items.filter(item => isFieldVisible(item.value, item.label));
    
    return filteredItems.map((item, index) => (
      <Descriptions.Item key={index} label={item.label}>
        {item.value}
      </Descriptions.Item>
    ));
  };

  const renderAdminProfile = () => (
    <Row gutter={[24, 24]}>
      <Col span={24} md={16}>
        <Card
          title={t('profile.profileInfo')}
          /*extra={
            <Button
              icon={<EditOutlined />}
              onClick={() => setEditModalVisible(true)}
            >
              {t('common.edit')}
            </Button>
          }*/
        >
          <Descriptions column={1} bordered>
            {renderFilteredDescriptions([
              { label: t('profile.username'), value: userProfile?.login },
              { label: t('common.firstName'), value: userProfile?.firstName || t('profile.notProvided') },
              { label: t('common.lastName'), value: userProfile?.lastName || t('profile.notProvided') },
              { label: t('common.email'), value: userProfile?.email },
              { label: t('profile.roles'), value: userProfile?.authorities?.join(', ') }
            ])}
          </Descriptions>
        </Card>
      </Col>
      {isFieldVisible("actions", t('common.actions')) && (
        <Col span={24} md={8}>
          <Card title={t('profile.quickActions')}>
            <Space direction="vertical" className="full-width">
              {isFieldVisible(t('profile.changePassword'), t('profile.changePassword')) && (
                <Button
                  icon={<LockOutlined />}
                  onClick={() => setPasswordModalVisible(true)}
                  block
                >
                  {t('profile.changePassword')}
                </Button>
              )}
            </Space>
          </Card>
        </Col>
      )}
    </Row>
  );

  const renderAgencyProfile = () => (
    <Row gutter={[24, 24]}>
      <Col span={24} md={12}>
        <Card
          title={t('profile.agencyInfo')}
          /*extra={
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                form.setFieldsValue(agencyData);
                setEditModalVisible(true);
              }}
            >
              {t('common.edit')}
            </Button>
          }*/
        >
          <Descriptions column={1} bordered>
            {renderFilteredDescriptions([
              { label: t('agency.profile.agencyName'), value: agencyData?.agencyName },
              { label: t('profile.licenceNumber'), value: agencyData?.licenceNumber },
              { label: t('common.email'), value: agencyData?.agencyEmail },
              { label: t('common.phone'), value: agencyData?.phoneNum },
              { label: t('common.address'), value: `${agencyData?.address}, ${agencyData?.zipCode} ${agencyData?.city}` },
              { label: t('profile.manager'), value: `${agencyData?.managerFirstName} ${agencyData?.managerLastName}` },
              { label: t('countries.country'), value: agencyData?.country?.country }
            ])}
            {isFieldVisible(agencyData?.status, t('common.status')) && (
              <Descriptions.Item label={t('common.status')}>
                {getStatusBadge(agencyData?.status || '')}
              </Descriptions.Item>
            )}
            {agencyData?.validationDateTime && isFieldVisible(agencyData.validationDateTime, t('profile.validatedOn')) && (
              <Descriptions.Item label={t('profile.validatedOn')}>
                {new Date(agencyData.validationDateTime).toLocaleDateString(i18n.language)}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      </Col>

      <Col span={24} md={12}>
        <Row gutter={[0, 24]}>
          {isFieldVisible(agencyData?.balance?.toString(), t('profile.availableBalance')) && (
            <Col span={24}>
              <Card>
                <Statistic
                  title={t('profile.availableBalance')}
                  value={agencyData?.balance || 0}
                  precision={2}
                  suffix={agencyData?.currency?.symbol || '€'}
                  prefix={<DollarOutlined />}
                />
                <Divider />
                <Space>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => history.push('/agency/manual-payment')}
                  >
                    {t('profile.addFunds')}
                  </Button>
                  <Button
                    icon={<EyeOutlined />}
                    onClick={() => history.push('/agency/invoice-history')}
                  >
                    {t('profile.viewHistory')}
                  </Button>
                </Space>
              </Card>
            </Col>
          )}

          {isFieldVisible("actions", t('common.actions')) && (
            <Col span={24}>
              <Card title={t('profile.quickActions')}>
                <Space direction="vertical" className="full-width">
                  {isFieldVisible(t('profile.changePassword'), t('profile.changePassword')) && (
                    <Button
                      icon={<LockOutlined />}
                      onClick={() => setPasswordModalVisible(true)}
                      block
                    >
                      {t('profile.changePassword')}
                    </Button>
                  )}

                  {isFieldVisible(t('profile.manageLicence'), t('profile.manageLicence')) && (
                    <Button style={{visibility:'hidden'}}
                      icon={<FileTextOutlined />}
                      onClick={() => setLicenceModalVisible(true)}
                      block
                    >
                      {t('profile.manageLicence')}
                    </Button>
                  )}
                </Space>
              </Card>
            </Col>
          )}
        </Row>
      </Col>
    </Row>
  );

  return (
    <>
      {/* Header avec image de fond */}
      <div className="profile-header" />

      {/* En-tête du profil */}
      <Card
        className="profile-card-head"
        bodyStyle={{ display: 'none' }}
        title={
          <Row justify="space-between" align="middle">
            <Col>
              <div className="profile-info-container">
                <Avatar
                  size={74}
                  icon={<UserOutlined />}
                  src={logoUrl || userProfile?.imageUrl}
                />
                <div>
                  <Title level={4} className="profile-user-title">
                    {isAgency 
                      ? `${agencyData?.managerFirstName} ${agencyData?.managerLastName}`
                      : `${userProfile?.firstName} ${userProfile?.lastName}` || userProfile?.login
                    }
                  </Title>
                  <Text className="profile-user-role">
                    {isAdmin ? t('profile.administrator') : t('profile.agencyRole')}
                  </Text>
                </div>
              </div>
            </Col>
          </Row>
        }
      />

      {/* Contenu principal */}
      <div className="profile-main-content">
        {isAdmin ? renderAdminProfile() : renderAgencyProfile()}
      </div>

      {/* Modal d'édition du profil */}
      <Modal
        title={t('profile.editProfile')}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateProfile}
        >
          {isAgency ? (
            <>
              <Form.Item name="agencyName" label={t('agency.profile.agencyName')} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="agencyEmail" label={t('common.email')} rules={[{ required: true, type: 'email' }]}>
                <Input />
              </Form.Item>
              <Form.Item name="phoneNum" label={t('common.phone')}>
                <Input />
              </Form.Item>
              <Form.Item name="address" label={t('common.address')}>
                <Input.TextArea rows={3} />
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="zipCode" label={t('profile.postalCode')}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="city" label={t('common.city')}>
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="managerFirstName" label={t('profile.managerFirstName')}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="managerLastName" label={t('profile.managerLastName')}>
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </>
          ) : (
            <>
              <Form.Item name="firstName" label={t('common.firstName')}>
                <Input />
              </Form.Item>
              <Form.Item name="lastName" label={t('common.lastName')}>
                <Input />
              </Form.Item>
              <Form.Item name="email" label={t('common.email')} rules={[{ required: true, type: 'email' }]}>
                <Input />
              </Form.Item>
            </>
          )}
          <div className="profile-form-footer">
            <Space>
              <Button onClick={() => setEditModalVisible(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {t('common.save')}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Modal de changement de mot de passe */}
      <Modal
        title={t('profile.changePassword')}
        open={passwordModalVisible}
        onCancel={() => setPasswordModalVisible(false)}
        footer={null}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handleChangePassword}
        >
          <Form.Item
            name="currentPassword"
            label={t('profile.currentPassword')}
            rules={[{ required: true, message: t('profile.currentPasswordRequired') }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label={t('profile.newPassword')}
            rules={[
              { required: true, message: t('profile.newPasswordRequired') },
              { min: 6, message: t('profile.passwordMinLength6') }
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label={t('profile.confirmNewPassword')}
            dependencies={['newPassword']}
            rules={[
              { required: true, message: t('profile.confirmPasswordRequired') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('profile.passwordsMismatch')));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
          <div className="profile-form-footer">
            <Space>
              <Button onClick={() => setPasswordModalVisible(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {t('common.edit')}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Modal de gestion de licence (pour les agences) */}
      {isAgency && (
        <Modal
          title={t('profile.licenceManagement')}
          open={licenceModalVisible}
          onCancel={() => setLicenceModalVisible(false)}
          footer={null}
        >
          <div className="profile-upload-container">
            <Upload
              name="licence"
              listType="picture"
              fileList={fileList}
              onChange={handleUploadChange}
              beforeUpload={beforeUpload}
            >
              <Button icon={<UploadOutlined />} size="large">
                {t('profile.uploadLicence')}
              </Button>
            </Upload>
            <div className="profile-upload-info">
              <Text type="secondary">
                {t('profile.acceptedFormats')}
              </Text>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Profile;